import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, forkJoin, from, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import {
	BatchUploadProgress,
	CompleteUploadRequest,
	CreateUploadSessionRequest,
	CreateUploadSessionResponse,
	FileSystemNode,
	FolderUploadProgress,
	InitiateUploadRequest,
	InitiateUploadResponse,
	MultipartUploadResponse,
	PostUploadResponse,
	UploadOptions,
	UploadPartResult,
	UploadProgress,
	UploadSignal,
	UploadStatus
} from '../file-library.model';
import { FileLibraryStore } from '../file-library.store';
import {
	DEFAULT_CHUNK_SIZE,
	MULTIPART_THRESHOLD,
	calculateChunkChecksum,
	calculateProgress,
	determineUploadStrategy,
	getChunkBoundaries,
	readChunkAsArrayBuffer
} from '../utils/chunk.utils';

/**
 * Active upload reference for tracking and cancellation
 */
interface ActiveUploadRef {
	subscription: Subscription;
	uploadId?: string;
	aborted: boolean;
	progress$: BehaviorSubject<UploadProgress>;
}

/**
 * File Upload Service
 * Handles Direct-to-Storage (DTS) uploads with progress tracking, chunking, and retry logic.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadService {
	private readonly endpoint = `${environment.apiUrl}/site/${environment.organizationId}/file-library`;

	// Active upload tracking
	private activeUploads = new Map<string, ActiveUploadRef>();

	// Upload progress streams
	private uploadProgress$ = new Subject<UploadProgress>();

	constructor(private readonly http: HttpClient, private readonly store: FileLibraryStore) {}

	/**
	 * Get upload progress observable for external subscribers
	 */
	getUploadProgress(): Observable<UploadProgress> {
		return this.uploadProgress$.asObservable();
	}

	/**
	 * Uploads a single file with progress tracking
	 */
	uploadFile(file: File, directoryId?: string, options?: UploadOptions): Observable<UploadSignal> {
		const tempId = uuidv4();
		const progress$ = new BehaviorSubject<UploadProgress>(this.createInitialProgress(tempId, file));

		// Emit initial progress
		this.uploadProgress$.next(progress$.value);

		// Store upload reference
		const uploadRef: ActiveUploadRef = {
			subscription: new Subscription(),
			uploadId: undefined,
			aborted: false,
			progress$
		};
		this.activeUploads.set(tempId, uploadRef);

		// Determine upload strategy
		const strategy = determineUploadStrategy(file.size);
		const parts = strategy === 'multipart' ? Math.ceil(file.size / DEFAULT_CHUNK_SIZE) : undefined;

		const upload$ = this.initiateUpload({
			filename: file.name,
			contentType: file.type || 'application/octet-stream',
			size: file.size,
			strategy,
			parts,
			directoryId,
			sessionId: options?.sessionId,
			relativePath: options?.relativePath,
			initialStatus: 'published'
		}).pipe(
			tap(response => {
				uploadRef.uploadId = response.uploadId;
				this.updateProgress(tempId, { status: 'uploading' });
			}),
			switchMap(initResponse => {
				if (initResponse.strategy === 'post') {
					return this.uploadPost(file, initResponse, tempId);
				} else {
					return this.uploadMultipart(file, initResponse, tempId);
				}
			}),
			switchMap(parts => {
				if (options?.sessionId) {
					// Session upload - defer completion
					this.updateProgress(tempId, { status: 'complete', percentage: 100 });
					return of<UploadSignal>({ kind: 'session' });
				}

				this.updateProgress(tempId, { status: 'processing' });
				const uploadId = uploadRef.uploadId!;
				return this.completeUpload(uploadId, parts).pipe(map(node => ({ kind: 'file', node } as UploadSignal)));
			}),
			tap(signal => {
				if (signal.kind === 'file') {
					this.updateProgress(tempId, { status: 'complete', percentage: 100 });
				}
			}),
			catchError(error => {
				const isCancelled = uploadRef.aborted || (error as { isCancelled?: boolean })?.isCancelled;
				this.updateProgress(tempId, {
					status: isCancelled ? 'cancelled' : 'error',
					error: error.message || 'Upload failed'
				});

				// Abort upload on error if not already cancelled
				if (uploadRef.uploadId && !uploadRef.aborted) {
					this.abortUpload(uploadRef.uploadId).pipe(take(1)).subscribe();
				}

				throw error;
			}),
			finalize(() => {
				this.activeUploads.delete(tempId);
			})
		);

		return new Observable<UploadSignal>(observer => {
			const sub = upload$.subscribe(observer);
			uploadRef.subscription = sub;
			return () => sub.unsubscribe();
		});
	}

	/**
	 * Uploads multiple files with batch progress tracking
	 */
	uploadFiles(files: File[], directoryId?: string, options?: { concurrency?: number }): Observable<BatchUploadProgress> {
		const concurrency = options?.concurrency ?? 3;
		const batchProgress$ = new BehaviorSubject<BatchUploadProgress>({
			totalFiles: files.length,
			completedFiles: 0,
			failedFiles: 0,
			totalBytes: files.reduce((sum, f) => sum + f.size, 0),
			bytesUploaded: 0,
			percentage: 0,
			files: files.map((f, i) => this.createInitialProgress(`batch-${i}`, f))
		});

		const uploads = files.map((file, index) => {
			const fileId = `batch-${index}`;
			return this.uploadFile(file, directoryId).pipe(
				tap(() => {
					const current = batchProgress$.value;
					const updatedFiles = [...current.files];
					updatedFiles[index] = {
						...updatedFiles[index],
						status: 'complete',
						percentage: 100,
						bytesUploaded: file.size
					};
					batchProgress$.next({
						...current,
						completedFiles: current.completedFiles + 1,
						bytesUploaded: current.bytesUploaded + file.size,
						percentage: calculateProgress(current.bytesUploaded + file.size, current.totalBytes),
						files: updatedFiles
					});
				}),
				catchError(error => {
					const current = batchProgress$.value;
					const updatedFiles = [...current.files];
					updatedFiles[index] = {
						...updatedFiles[index],
						status: 'error',
						error: error.message
					};
					batchProgress$.next({
						...current,
						failedFiles: current.failedFiles + 1,
						files: updatedFiles
					});
					return of(null);
				})
			);
		});

		// Execute uploads with concurrency limit
		return from(uploads).pipe(
			mergeMap(upload$ => upload$, concurrency),
			finalize(() => batchProgress$.complete()),
			map(() => batchProgress$.value)
		);
	}

	/**
	 * Uploads a folder with directory structure preservation
	 */
	uploadFolder(files: FileList | File[], rootDirectoryId?: string): Observable<FolderUploadProgress> {
		const fileArray = Array.from(files);
		const progress$ = new BehaviorSubject<FolderUploadProgress>({
			sessionId: '',
			rootDirectoryId: rootDirectoryId ?? '',
			totalFiles: fileArray.length,
			completedFiles: 0,
			failedFiles: 0,
			totalBytes: fileArray.reduce((sum, f) => sum + f.size, 0),
			bytesUploaded: 0,
			percentage: 0,
			foldersCreated: 0,
			files: fileArray.map((f, i) => this.createInitialProgress(`folder-${i}`, f))
		});

		return this.createUploadSession({
			rootDirectoryId,
			maxFiles: fileArray.length,
			maxTotalSize: progress$.value.totalBytes
		}).pipe(
			tap(session => {
				progress$.next({ ...progress$.value, sessionId: session.sessionId });
			}),
			switchMap(session => {
				// Upload each file with session context
				const uploads = fileArray.map((file, index) => {
					const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
					return this.uploadFile(file, rootDirectoryId, {
						sessionId: session.sessionId,
						relativePath
					}).pipe(
						tap(() => {
							const current = progress$.value;
							const updatedFiles = [...current.files];
							updatedFiles[index] = {
								...updatedFiles[index],
								status: 'complete',
								percentage: 100,
								bytesUploaded: file.size
							};
							progress$.next({
								...current,
								completedFiles: current.completedFiles + 1,
								bytesUploaded: current.bytesUploaded + file.size,
								percentage: calculateProgress(current.bytesUploaded + file.size, current.totalBytes),
								files: updatedFiles
							});
						}),
						catchError(error => {
							const current = progress$.value;
							const updatedFiles = [...current.files];
							updatedFiles[index] = {
								...updatedFiles[index],
								status: 'error',
								error: error.message
							};
							progress$.next({
								...current,
								failedFiles: current.failedFiles + 1,
								files: updatedFiles
							});
							return of(null);
						})
					);
				});

				return forkJoin(uploads).pipe(
					switchMap(() => this.completeUploadSession(session.sessionId)),
					map(() => progress$.value)
				);
			})
		);
	}

	/**
	 * Cancels an active upload
	 */
	cancelUpload(tempId: string): void {
		const uploadRef = this.activeUploads.get(tempId);
		if (uploadRef) {
			uploadRef.aborted = true;
			uploadRef.subscription.unsubscribe();

			if (uploadRef.uploadId) {
				this.abortUpload(uploadRef.uploadId).pipe(take(1)).subscribe();
			}

			this.updateProgress(tempId, { status: 'cancelled' });
			this.activeUploads.delete(tempId);
		}
	}

	/**
	 * Cancels all active uploads
	 */
	cancelAllUploads(): void {
		for (const [tempId] of this.activeUploads) {
			this.cancelUpload(tempId);
		}
	}

	/**
	 * Gets the number of active uploads
	 */
	getActiveUploadCount(): number {
		return this.activeUploads.size;
	}

	/**
	 * Initiates a Direct-to-Storage upload
	 */
	private initiateUpload(request: InitiateUploadRequest): Observable<InitiateUploadResponse> {
		return this.http.post<InitiateUploadResponse>(`${this.endpoint}/upload/initiate`, request);
	}

	/**
	 * Completes a Direct-to-Storage upload
	 */
	private completeUpload(uploadId: string, parts?: UploadPartResult[]): Observable<FileSystemNode> {
		const request: CompleteUploadRequest = {
			uploadId,
			key: '', // API will use uploadId to lookup key
			parts
		};
		return this.http.post<FileSystemNode>(`${this.endpoint}/upload/complete`, request);
	}

	/**
	 * Aborts a Direct-to-Storage upload
	 */
	private abortUpload(uploadId: string): Observable<void> {
		return this.http.delete<void>(`${this.endpoint}/upload/abort/${uploadId}`);
	}

	/**
	 * Creates an upload session for folder uploads
	 */
	private createUploadSession(request: CreateUploadSessionRequest): Observable<CreateUploadSessionResponse> {
		return this.http.post<CreateUploadSessionResponse>(`${this.endpoint}/upload/session`, request);
	}

	/**
	 * Completes an upload session
	 */
	private completeUploadSession(sessionId: string): Observable<void> {
		return this.http.post<void>(`${this.endpoint}/upload/complete`, { sessionId });
	}

	/**
	 * Uploads using POST strategy (for small files)
	 */
	private uploadPost(file: File, uploadData: PostUploadResponse, tempId: string): Observable<undefined> {
		return new Observable<undefined>(observer => {
			const formData = new FormData();

			Object.entries(uploadData.fields).forEach(([key, value]) => {
				formData.append(key, value as string);
			});
			formData.append('file', file);

			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener('progress', event => {
				if (event.lengthComputable) {
					this.updateProgress(tempId, {
						bytesUploaded: event.loaded,
						percentage: calculateProgress(event.loaded, event.total)
					});
				}
			});

			xhr.addEventListener('load', () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					observer.next(undefined);
					observer.complete();
				} else {
					observer.error(new Error(`Upload failed with status ${xhr.status}`));
				}
			});

			xhr.addEventListener('error', () => {
				observer.error(new Error('Upload failed'));
			});

			xhr.addEventListener('abort', () => {
				const error = new Error('Upload cancelled') as Error & { isCancelled: boolean };
				error.isCancelled = true;
				observer.error(error);
			});

			xhr.open('POST', uploadData.url);
			xhr.send(formData);

			return () => xhr.abort();
		});
	}

	/**
	 * Uploads using multipart strategy (for large files)
	 * Uses parallel uploads with a concurrency limit for better performance.
	 */
	private uploadMultipart(file: File, uploadData: MultipartUploadResponse, tempId: string): Observable<UploadPartResult[]> {
		const CONCURRENCY_LIMIT = 5;
		const uploadedParts: UploadPartResult[] = [];
		let totalUploaded = 0;

		return new Observable<UploadPartResult[]>(observer => {
			const activeSubs: Subscription[] = [];
			const aborted = { value: false };
			let batchIndex = 0;

			const uploadBatch = (): void => {
				if (aborted.value) return;

				const startIdx = batchIndex * CONCURRENCY_LIMIT;
				if (startIdx >= uploadData.parts.length) {
					// Sort by part number to ensure correct order
					uploadedParts.sort((a, b) => a.number - b.number);
					observer.next(uploadedParts);
					observer.complete();
					return;
				}

				const batch = uploadData.parts.slice(startIdx, startIdx + CONCURRENCY_LIMIT);
				let completedInBatch = 0;
				let batchFailed = false;

				batch.forEach(part => {
					const { start, end } = getChunkBoundaries(file.size, part.number);
					const chunk = file.slice(start, end);
					const chunkSize = chunk.size;

					const sub = this.uploadPart(chunk, part.url, part.number, file.type).subscribe({
						next: result => {
							if (aborted.value || batchFailed) return;

							uploadedParts.push(result);
							totalUploaded += chunkSize;
							this.updateProgress(tempId, {
								bytesUploaded: totalUploaded,
								percentage: calculateProgress(totalUploaded, file.size)
							});

							completedInBatch++;
							if (completedInBatch === batch.length) {
								batchIndex++;
								uploadBatch();
							}
						},
						error: error => {
							if (!batchFailed) {
								batchFailed = true;
								observer.error(error);
							}
						}
					});

					activeSubs.push(sub);
				});
			};

			uploadBatch();

			return () => {
				aborted.value = true;
				activeSubs.forEach(sub => sub.unsubscribe());
			};
		});
	}

	/**
	 * Uploads a single part in multipart upload
	 */
	private uploadPart(chunk: Blob, url: string, partNumber: number, contentType: string): Observable<UploadPartResult> {
		return new Observable<UploadPartResult>(observer => {
			const reader = new FileReader();
			let xhr: XMLHttpRequest | null = null;

			reader.onload = async () => {
				try {
					const arrayBuffer = reader.result as ArrayBuffer;
					const checksum = await calculateChunkChecksum(arrayBuffer);

					xhr = new XMLHttpRequest();

					xhr.addEventListener('load', () => {
						if (xhr && xhr.status >= 200 && xhr.status < 300) {
							const eTag = xhr.getResponseHeader('ETag');
							if (!eTag) {
								observer.error(new Error(`Part ${partNumber} missing ETag header`));
								return;
							}

							observer.next({
								number: partNumber,
								eTag,
								checksumSHA256: checksum
							});
							observer.complete();
						} else {
							observer.error(new Error(`Part ${partNumber} upload failed with status ${xhr?.status}`));
						}
					});

					xhr.addEventListener('error', () => {
						observer.error(new Error(`Part ${partNumber} upload failed`));
					});

					xhr.addEventListener('abort', () => {
						const error = new Error(`Part ${partNumber} cancelled`) as Error & { isCancelled: boolean };
						error.isCancelled = true;
						observer.error(error);
					});

					xhr.open('PUT', url);
					xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
					xhr.send(chunk);
				} catch (error) {
					observer.error(new Error(`Part ${partNumber} checksum failed: ${error}`));
				}
			};

			reader.onerror = () => {
				observer.error(new Error(`Part ${partNumber} read failed`));
			};

			reader.readAsArrayBuffer(chunk);

			return () => {
				if (xhr) xhr.abort();
				reader.abort();
			};
		});
	}

	/**
	 * Creates initial progress state
	 */
	private createInitialProgress(fileId: string, file: File): UploadProgress {
		return {
			fileId,
			filename: file.name,
			bytesUploaded: 0,
			totalBytes: file.size,
			percentage: 0,
			status: 'pending',
			startTime: Date.now()
		};
	}

	/**
	 * Updates progress for an upload
	 */
	private updateProgress(tempId: string, updates: Partial<UploadProgress>): void {
		const uploadRef = this.activeUploads.get(tempId);
		if (uploadRef) {
			const current = uploadRef.progress$.value;
			const updated = { ...current, ...updates };

			// Calculate speed if we have time data
			if (updated.startTime && updated.bytesUploaded > 0) {
				const elapsed = (Date.now() - updated.startTime) / 1000;
				updated.speed = elapsed > 0 ? updated.bytesUploaded / elapsed : 0;
			}

			uploadRef.progress$.next(updated);
			this.uploadProgress$.next(updated);
		}
	}
}
